<script setup lang="ts">
  import { ref, computed, useTemplateRef } from 'vue'
  import type { SetFieldConfigRequestPayload } from '@gat-ship/types'
  import type { ResolvedField } from '../model/types'
  import { DataList, SectionHeader } from '@gat-ship/vue-components'
  import { useCustomFieldsView } from '../composables/useCustomFieldsView'
  import CustomFieldListItem from '../components/CustomFieldListItem.vue'
  import CustomFieldModalEditSystem from '../components/modals/CustomFieldModalEditSystem.vue'

  const {
    loading,
    loadingFieldIds,
    resolvedFields,
    setFieldConfig,
    resetFieldConfig
  } = useCustomFieldsView()

  // ── Filtered Fields ───────────────────────────────────────────

  const filteredFields = computed(() =>
    resolvedFields.value.filter((f) => f.origin === 'system')
  )

  function isFieldLoading(field: ResolvedField) {
    return (
      !!field.customFieldId && loadingFieldIds.value.has(field.customFieldId)
    )
  }

  // ── Dialog State ──────────────────────────────────────────────

  const editSystemDialogField = ref<ResolvedField | null>(null)
  const editSystemDialogRef =
    useTemplateRef<InstanceType<typeof CustomFieldModalEditSystem>>(
      'editSystemDialog'
    )

  // ── Handlers ──────────────────────────────────────────────────

  function handleFieldClick(field: ResolvedField) {
    if (!field.configurable) return
    editSystemDialogField.value = field
    editSystemDialogRef.value?.show(field)
  }

  async function handleSaveConfig(payload: SetFieldConfigRequestPayload) {
    if (!editSystemDialogField.value) return
    await setFieldConfig(
      editSystemDialogField.value.origin,
      editSystemDialogField.value.fieldName,
      payload
    )
  }

  async function handleResetConfig() {
    if (!editSystemDialogField.value) return
    await resetFieldConfig(
      editSystemDialogField.value.origin,
      editSystemDialogField.value.fieldName
    )
  }
</script>

<template>
  <SectionHeader
    title="System Fields"
    description="Configure system fields for your organization."
  />
  <DataList :loading="loading" :empty="filteredFields.length === 0">
    <CustomFieldListItem
      v-for="field in filteredFields"
      :key="field.fieldName"
      :field="field"
      :loading="isFieldLoading(field)"
      @select="handleFieldClick(field)"
    />
  </DataList>

  <CustomFieldModalEditSystem
    ref="editSystemDialog"
    :field="editSystemDialogField"
    @save-config="handleSaveConfig"
    @reset-config="handleResetConfig"
  />
</template>
